import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';

import { useGeoCodingContext } from '../context/GeoCodingContext';
import useFileReader from './useFileReader';
import useGeocodeSSE from './useGeocodeSSE';
import useGeocoderAppState from './useGeocoderAppState';

import { blobToGeoJSON } from 'features/geocoder/utils/geojsonUtils';
import {
    parseShapefileZip,
    validateShapefileZip,
    geojsonToGrid,
    makeCsvBlobFromRows,
    readPrjFromZip,
    detectEpsgFromPrj,
} from 'features/geocoder/utils/shpUtils';

const GUESS_RX_X = [/^(x|lon|lng|경도|x3857|x4326)$/i, /(x.?coord|coord.?x)/i];
const GUESS_RX_Y = [/^(y|lat|위도|y3857|y4326)$/i, /(y.?coord|coord.?y)/i];

const defaultOption = (options = []) => options[0] || null;

export default function useUploadWorkflow({
    encodingOptions,
    delimiterOptions,
    geocodingTypeOptions,
    coordSystemOptions,
    downloadFormatOptions,
}) {
    const {
        geocodingType, setGeocodingType, coordSystem, setCoordSystem,
        file, setFile, isShapefile, setIsShapefile, dataEpsg, setDataEpsg, swapXY, setSwapXY,
        gridRows, setGridRows, gridColumns, setGridColumns,
        previewGeoJSON, setPreviewGeoJSON, resultGeoJSON, setResultGeoJSON,
        resultBlob, setResultBlob, resultMeta, setResultMeta, resultName, setResultName,
        lastStatus, setLastStatus,
        shapeAddrOpts, setShapeAddrOpts, shapeAddrField, setShapeAddrField,
        sheetAddrOpts, setSheetAddrOpts, sheetAddrField, setSheetAddrField,
        xyFieldOptions, setXyFieldOptions, xField, setXField, yField, setYField, isFileDeletedAfterUpload, setIsFileDeletedAfterUpload 
    } = useGeoCodingContext();

    const [tab, setTab] = useState('forward');
    const [encoding, setEncoding] = useState(defaultOption(encodingOptions));
    const [delimiter, setDelimiter] = useState(defaultOption(delimiterOptions));
    const [downloadCoordSystem, setDownloadCoordSystem] = useState(defaultOption(coordSystemOptions));
    const [downloadFormat, setDownloadFormat] = useState(defaultOption(downloadFormatOptions));

    const {
        read,
        clearState: clearFileReaderState,
    } = useFileReader();

    const { progress, setProgress, status, run, cancel } = useGeocodeSSE(async (blob, meta) => {
        const gj = await blobToGeoJSON(blob, { srcEPSG: 'EPSG:3857', dstEPSG: 'EPSG:3857' });
        setPreviewGeoJSON(gj);
        setResultGeoJSON(gj);
        setDataEpsg('EPSG:3857');
        setSwapXY(false);
        setResultBlob(blob);
        setResultMeta(meta || null);

        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        const fallback = `${tab === 'forward' ? 'geocode' : 'reverse'}_${downloadCoordSystem?.name || coordSystem?.name}_${ts}`;
        setResultName(meta?.fileNameNoExt || fallback);
    });

    useEffect(() => {
        if (typeof status === 'string' && status.trim()) {
            setLastStatus(status);
        }
    }, [status, setLastStatus]);

    const unifiedAddrOptions = isShapefile ? shapeAddrOpts : sheetAddrOpts;
    const unifiedAddrField = isShapefile ? shapeAddrField : sheetAddrField;
    const setUnifiedAddrField = (field) => {
        if (isShapefile) setShapeAddrField(field);
        else setSheetAddrField(field);
    };

    const guessField = (opts, regexes) => {
        const idx = opts.findIndex((o) => regexes.some((rx) => rx.test(String(o.value))));
        return idx >= 0 ? opts[idx] : opts[0] || null;
    };

    const resetResultForRun = () => {
        setResultBlob(null);
        setResultMeta(null);
        setPreviewGeoJSON(null);
        setResultGeoJSON(null);
        setResultName('geocode_result');
    };

    const blobToWorkbook = async (blob) => {
        try {
            const ab = await blob.arrayBuffer();
            return XLSX.read(ab, { type: 'array' });
        } catch (e) {
            console.error("Error reading workbook:", e);
            setLastStatus('파일을 읽는 중 오류가 발생했습니다. 파일이 손상되었거나 지원되지 않는 형식일 수 있습니다.');
            return null;
        }
    };
    const workbookToBlob = (wb, bookType) => {
        const out = XLSX.write(wb, { bookType, type: 'array' });
        const mime =
            bookType === 'xlsx'
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'text/csv';
        return new Blob([out], { type: mime });
    };
    const saveBlob = (blob, fileName) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };
    const extFromMetaOrFormat = (meta) => {
        if (meta?.ext) return meta.ext;
        const fmt = (downloadFormat?.value || '').toLowerCase();
        if (fmt.includes('xlsx')) return 'xlsx';
        if (fmt.includes('csv')) return 'csv';
        return 'bin';
    };

    const handleDownloadOriginal = async () => {
        if (!resultBlob) return;
        const base = resultMeta?.fileNameNoExt || resultName;
        const ext = extFromMetaOrFormat(resultMeta);
        saveBlob(resultBlob, `${base}.${ext}`);
    };
    const handleDownloadCSV = async () => {
        if (!resultBlob) return;
        const wb = await blobToWorkbook(resultBlob);
        if (!wb) return;
        const blob = workbookToBlob(wb, 'csv');
        const base = resultMeta?.fileNameNoExt || resultName;
        saveBlob(blob, `${base}.csv`);
    };
    const handleDownloadXLSX = async () => {
        if (!resultBlob) return;
        const wb = await blobToWorkbook(resultBlob);
        if (!wb) return;
        const blob = workbookToBlob(wb, 'xlsx');
        const base = resultMeta?.fileNameNoExt || resultName;
        saveBlob(blob, `${base}.xlsx`);
    };
    const handleDownloadGeoJSON = () => {
        if (!previewGeoJSON) return;
        const blob = new Blob([JSON.stringify(previewGeoJSON)], { type: 'application/geo+json' });
        const base = resultMeta?.fileNameNoExt || resultName;
        saveBlob(blob, `${base}.geojson`);
    };

    const getUploadReadyFile = () => {
        if (!isShapefile) return file;
        if (!file || !gridColumns?.length) return file;

        const headers = gridColumns.map((c) => c.value);
        const csvBlob = makeCsvBlobFromRows(gridRows, headers);
        const safeName = `${file.name.replace(/\.zip$/i, '') || 'shapefile'}.csv`;
        return new File([csvBlob], safeName, { type: 'text/csv' });
    };

    const onDrop = useCallback(
        async (accepted) => {
            const f = accepted?.[0];
            setFile(f);
            if (!f) return;

            setResultBlob(null);
            setResultMeta(null);
            setPreviewGeoJSON(null);

            const name = f.name.toLowerCase();
            const isZip = name.endsWith('.zip');

            if (isZip) {
                setIsShapefile(true);

                const check = await validateShapefileZip(f);
                if (!check.ok) {
                    setIsShapefile(false);
                    setPreviewGeoJSON(null);
                    setResultGeoJSON(null);
                    setGridColumns([]);
                    setGridRows([]);
                    setShapeAddrOpts([]);
                    setShapeAddrField(null);
                    setXyFieldOptions([]);
                    setXField(null);
                    setYField(null);
                    setLastStatus(check.errors[0] || '유효한 Shapefile ZIP이 아닙니다.');
                    return;
                }
                if (check.warnings?.length) setLastStatus(check.warnings.join(' | '));

                const prj = await readPrjFromZip(f);
                const guessed = detectEpsgFromPrj(prj) || 'EPSG:3857';
                setDataEpsg(guessed);
                setSwapXY(false);

                const gj = await parseShapefileZip(f);
                setPreviewGeoJSON(gj);
                const { columns: shpCols, rows: shpRows } = geojsonToGrid(gj);
                setGridColumns(shpCols); 
                setGridRows(shpRows);

                setShapeAddrOpts(shpCols);
                const addrGuess = shpCols.find((c) => /주소|addr/i.test(String(c.value))) || shpCols[0];
                setShapeAddrField(addrGuess);

                const xyOpts = [...shpCols];
                setXyFieldOptions(xyOpts);
                setXField(guessField(xyOpts, GUESS_RX_X));
                setYField(guessField(xyOpts, GUESS_RX_Y));

                setSheetAddrOpts([]);
                setSheetAddrField(null);
                return;
            }

            setIsShapefile(false);
            setDataEpsg('EPSG:3857');
            setSwapXY(false);
            setShapeAddrOpts([]);
            setShapeAddrField(null);

            // CSV 업로드 시 구분자 자동 감지해 콤보박스에 반영
            try {
                const name = f.name?.toLowerCase?.() || '';
                if (name.endsWith('.csv')) {
                    const text = await f.text();
                    const sample = text.split(/\r?\n/).slice(0, 10).join('\n');
                    const candidates = [
                        { name: 'comma', char: ',' },
                        { name: 'semicolon', char: ';' },
                        { name: 'pipe', char: '|' },
                        { name: 'tab', char: '\t' },
                    ];
                    const scores = candidates.map((c) => ({
                        name: c.name,
                        count: (sample.match(new RegExp(`\\${c.char}`, 'g')) || []).length,
                    }));
                    const best = scores.reduce(
                        (max, cur) => (cur.count > max.count ? cur : max),
                        { name: null, count: -1 }
                    );
                    if (best.count > 0) {
                        const next =
                            delimiterOptions.find((opt) => opt.name === best.name) || delimiterOptions[0];
                        if (next) setDelimiter(next);
                    }
                }
            } catch (e) {
                // 감지 실패 시 무시하고 기존 값 사용
            }

            await read(f, encoding?.value);
        },
        [
            delimiterOptions, encoding?.value, read, setSheetAddrField, setSheetAddrOpts, 
            setFile, setResultBlob, setResultMeta, setPreviewGeoJSON, setIsShapefile, 
            setGridColumns, setGridRows, setDataEpsg, setSwapXY, setShapeAddrOpts, 
            setShapeAddrField, setXyFieldOptions, setXField, setYField, setLastStatus, 
            setResultGeoJSON, setDelimiter
        ]
    );

    useEffect(() => {
        if (!file || isShapefile) return;
        if (!Array.isArray(gridColumns) || gridColumns.length === 0) return;

        const xyOpts = gridColumns;
        setXyFieldOptions(xyOpts);
        setXField(guessField(xyOpts, GUESS_RX_X));
        setYField(guessField(xyOpts, GUESS_RX_Y));
    }, [gridColumns, file, isShapefile, setXyFieldOptions, setXField, setYField]);

    useEffect(() => {
        const isZip = !!file && /\.zip$/i.test(file.name);
        if (file && !isShapefile && !isZip && encoding?.value) {
            read(file, encoding.value);
        }
    }, [encoding?.value, file, isShapefile, read]);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: {
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip'],
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
    });

    const handleUploadForward = async () => {
        const addrField = unifiedAddrField;
        if (!file || !addrField) return;

        resetResultForRun();

        const uploadFile = getUploadReadyFile();
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('encoding', encoding?.value);
        fd.append('delimeter', delimiter?.name);
        fd.append('col', addrField.idx);
        fd.append('addrType', geocodingType?.name);
        fd.append('srid', coordSystem?.name);
        fd.append('useHeader', true);
        fd.append('downloadFormat', downloadFormat?.name);
        fd.append('downloadCoordSrid', downloadCoordSystem?.name);
        fd.append('mode', 'forward');
        run(fd);
    };

    const handleUploadReverse = async () => {
        if (!file || !xField || !yField) return;

        resetResultForRun();

        const uploadFile = getUploadReadyFile();
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('encoding', encoding?.value);
        fd.append('delimeter', delimiter?.name);
        fd.append('xcol', xField.idx);
        fd.append('ycol', yField.idx);
        fd.append('addrType', geocodingType?.name);
        fd.append('srid', coordSystem?.name);
        fd.append('useHeader', true);
        fd.append('downloadFormat', downloadFormat?.name);
        fd.append('downloadCoordSrid', downloadCoordSystem?.name);
        fd.append('mode', 'reverse');
        run(fd, {
            uploadUrl: '/api/reverse/geoCodeByFile',
            sseUrl: '/api/reverse/geoCodeByFileStatus',
            downloadUrl: '/api/reverse/downloadFile',
        });
    };

    // 파일 업로드 초기화
    const clearUploadedData = useCallback(() => {
        console.log("useUploadWorkflow, 파일 업로드 초기화 실행")
        clearFileReaderState();

        // Context 상태 초기화
        setFile?.(null);
        setGridRows?.([]);
        setGridColumns?.([]);
        setPreviewGeoJSON?.(null);
        setResultGeoJSON?.(null);
        setResultBlob?.(null);
        setResultMeta?.(null);
        setLastStatus?.('');

        // 필드 초기화
        setShapeAddrOpts?.([]); setShapeAddrField?.(null);
        setSheetAddrOpts?.([]); setSheetAddrField?.(null);
        setXyFieldOptions?.([]); setXField?.(null); setYField?.(null);

        // 지오코딩 완료 상태 초기화
        setProgress?.(-1);

        // 파일 등록 후 삭제 여부 설정
        setIsFileDeletedAfterUpload(true);
    }, [
        clearFileReaderState, setFile, setGridRows, setGridColumns, 
        setPreviewGeoJSON, setResultGeoJSON, setResultBlob, setResultMeta, 
        setLastStatus, setShapeAddrOpts, setShapeAddrField, setSheetAddrOpts, 
        setSheetAddrField, setXyFieldOptions, setXField, setYField, setProgress, setIsFileDeletedAfterUpload
    ]);

    const canRunForward = tab === 'forward' && unifiedAddrOptions.length > 0 && !!file;
    const canRunReverse = tab === 'reverse' && xyFieldOptions.length > 0 && !!xField && !!yField && !!file;
    const canDownload = !!resultBlob;

    const validationMessage = useMemo(() => {
        if (!file) return '먼저 파일을 업로드하세요.';
        if (tab === 'forward') {
            if (!unifiedAddrField) return '지오코딩에 사용할 주소 컬럼을 선택하세요.';
        } else if (!xField || !yField) {
            return '좌표 컬럼(X/Y)을 모두 선택하세요.';
        }
        return null;
    }, [file, tab, unifiedAddrField, xField, yField]);

    const gridInfo = useMemo(
        () => ({
            rows: gridRows?.length ?? 0,
            columns: gridColumns?.length ?? 0,
        }),
        [gridRows, gridColumns]
    );

    const showDelimiterOption = useMemo(() => {
        if (!file || isShapefile) return false;
        const name = file.name?.toLowerCase?.() || '';
        return name.endsWith('.csv');
    }, [file, isShapefile]);

    const onEncodingChange = (value) => {
        const next = encodingOptions.find((opt) => opt.value === value) || encodingOptions[0];
        setEncoding(next);
    };
    const onDelimiterChange = (value) => {
        const next = delimiterOptions.find((opt) => opt.value === value) || delimiterOptions[0];
        setDelimiter(next);
    };
    const onGeocodingTypeChange = (value) => {
        const next = geocodingTypeOptions.find((opt) => opt.value === value) || geocodingTypeOptions[0];
        setGeocodingType(next);
    };
    const onCoordSystemChange = (value) => {
        const next = coordSystemOptions.find((opt) => opt.value === value) || coordSystemOptions[0];
        setCoordSystem(next);
    };
    const onDownloadFormatChange = (value) => {
        const next = downloadFormatOptions.find((opt) => opt.value === value) || downloadFormatOptions[0];
        setDownloadFormat(next);
    };
    const onDownloadCoordSystemChange = (value) => {
        const next = coordSystemOptions.find((opt) => opt.value === value) || coordSystemOptions[0];
        setDownloadCoordSystem(next);
    };
    const onAddrFieldChange = (value) => {
        const next = unifiedAddrOptions.find((opt) => opt.value === value) || null;
        setUnifiedAddrField(next);
    };
    const onXFieldChange = (value) => {
        const next = xyFieldOptions.find((opt) => opt.value === value) || null;
        setXField(next);
    };
    const onYFieldChange = (value) => {
        const next = xyFieldOptions.find((opt) => opt.value === value) || null;
        setYField(next);
    };

    return {
        tab, setTab, resultName, lastStatus, validationMessage,
        encoding, onEncodingChange, delimiter, onDelimiterChange,
        geocodingType, onGeocodingTypeChange, coordSystem, onCoordSystemChange,
        downloadFormat, onDownloadFormatChange, downloadCoordSystem, onDownloadCoordSystemChange,
        unifiedAddrOptions, unifiedAddrField,
        onAddrFieldChange: (value) => {
            const opts = isShapefile ? shapeAddrOpts : sheetAddrOpts;
            const setter = isShapefile ? setShapeAddrField : setSheetAddrField;
            const next = opts.find((opt) => opt.value === value) || null;
            setter(next);
        },
        xyFieldOptions, xField, yField, onXFieldChange, onYFieldChange,
        canRunForward, canRunReverse, canDownload,
        handleUploadForward, handleUploadReverse, handleDownloadOriginal,
        handleDownloadCSV, handleDownloadXLSX, handleDownloadGeoJSON,
        previewGeoJSON, gridRows, gridColumns, gridInfo, dataEpsg, swapXY,
        getRootProps, getInputProps, isDragActive,
        progress, status, cancel, showDelimiterOption, openFileDialog: open,
        file, resultGeoJSON, clearUploadedData, isFileDeletedAfterUpload, setIsFileDeletedAfterUpload
    };
}
